import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useDebounce from '../../../utils/useDebounce';
import { STATUS_OPTIONS, KIND_OPTIONS } from '../../../utils/sentEmails';

// The select value that swaps the kind dropdown for a free-text box. Templated
// sends are archived under their templateId, and there are too many of those
// (and they change too often) to list here.
const OTHER_KIND = '__other__';

// Same debounce the Logs toolbar uses for its typed filters.
const TYPING_DELAY = 500;

const isOtherKind = kind => !!kind && !KIND_OPTIONS.includes(kind);

// Native date inputs, as in LoyaltyToolbar: the @material-ui pickers other toolbars
// import are never mounted under a provider in this app and would throw.
const SentEmailsToolbar = ({ filters, onChange, onRefresh, refreshing }) => {
  const [recipientInput, setRecipientInput] = useState(filters.recipient || '');
  const [subjectInput, setSubjectInput] = useState(filters.subject || '');
  const [otherKindInput, setOtherKindInput] = useState(isOtherKind(filters.kind) ? filters.kind : '');
  const [kindMode, setKindMode] = useState(isOtherKind(filters.kind) ? 'other' : 'list');

  const debouncedRecipient = useDebounce(recipientInput, TYPING_DELAY);
  const debouncedSubject = useDebounce(subjectInput, TYPING_DELAY);
  const debouncedOtherKind = useDebounce(otherKindInput, TYPING_DELAY);

  useEffect(() => {
    if (debouncedRecipient !== (filters.recipient || '')) onChange({ recipient: debouncedRecipient });
    // Only when the typed value settles, not when `filters`/`onChange` identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRecipient]);

  useEffect(() => {
    if (debouncedSubject !== (filters.subject || '')) onChange({ subject: debouncedSubject });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSubject]);

  useEffect(() => {
    const kind = debouncedOtherKind.trim();
    if (kindMode === 'other' && kind !== (filters.kind || '')) onChange({ kind });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOtherKind]);

  const handleKindSelect = (value) => {
    if (value === OTHER_KIND) {
      setKindMode('other');
      onChange({ kind: otherKindInput.trim() });
      return;
    }
    setKindMode('list');
    onChange({ kind: value });
  };

  return (
    <div className="toolbar">
      <div className="field grow">
        <label htmlFor="sent-emails-recipient">
          Recipient
          <span className="control wide hasinput">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: 'rgba(0,0,0,.38)' }} aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" />
              <path d="M10.4 10.4L14 14" strokeLinecap="round" />
            </svg>
            <input
              id="sent-emails-recipient"
              type="text"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              placeholder="Full address, e.g. name@example.com"
              value={recipientInput}
              onChange={e => setRecipientInput(e.currentTarget.value)}
            />
          </span>
        </label>
      </div>

      <div className="field">
        <label htmlFor="sent-emails-subject">
          Subject contains
          <input
            id="sent-emails-subject"
            type="text"
            autoComplete="off"
            value={subjectInput}
            onChange={e => setSubjectInput(e.currentTarget.value)}
          />
        </label>
      </div>

      <div className="field">
        <label htmlFor="sent-emails-status">
          Status
          <select
            id="sent-emails-status"
            value={filters.status || ''}
            onChange={e => onChange({ status: e.currentTarget.value })}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="field">
        <label htmlFor="sent-emails-kind">
          Kind
          <select
            id="sent-emails-kind"
            value={kindMode === 'other' ? OTHER_KIND : (filters.kind || '')}
            onChange={e => handleKindSelect(e.currentTarget.value)}
          >
            <option value="">All</option>
            {KIND_OPTIONS.map(kind => <option key={kind} value={kind}>{kind}</option>)}
            <option value={OTHER_KIND}>Other…</option>
          </select>
        </label>
      </div>

      {kindMode === 'other' ? (
        <div className="field">
          <label htmlFor="sent-emails-kind-other">
            Template id
            <input
              id="sent-emails-kind-other"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="Exact template id"
              value={otherKindInput}
              onChange={e => setOtherKindInput(e.currentTarget.value)}
            />
          </label>
        </div>
      ) : null}

      {/* The server reads both dates as UTC calendar days, inclusive. */}
      <div className="field">
        <label htmlFor="sent-emails-from" title="UTC day, inclusive">
          From
          <input
            id="sent-emails-from"
            type="date"
            value={filters.from || ''}
            max={filters.to || undefined}
            onChange={e => onChange({ from: e.currentTarget.value })}
          />
        </label>
      </div>

      <div className="field">
        <label htmlFor="sent-emails-to" title="UTC day, inclusive">
          To
          <input
            id="sent-emails-to"
            type="date"
            value={filters.to || ''}
            min={filters.from || undefined}
            onChange={e => onChange({ to: e.currentTarget.value })}
          />
        </label>
      </div>

      <button type="button" className="btn ghost" onClick={onRefresh} disabled={refreshing}>
        {refreshing ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  );
};

SentEmailsToolbar.propTypes = {
  filters: PropTypes.shape({
    recipient: PropTypes.string,
    subject: PropTypes.string,
    status: PropTypes.string,
    kind: PropTypes.string,
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
};

SentEmailsToolbar.defaultProps = {
  refreshing: false,
};

export default SentEmailsToolbar;
